import React, { useEffect, useState } from 'react';
import { X, Cable as CableIcon, ArrowLeftRight, Trash2, Edit } from 'lucide-react';
import { PlacedEquipment, EquipmentFace } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import { Port } from '../../types/portCapabilities';
import { PortStage } from './PortStage';
import { makeTemplateForPorts } from '../../utils/layoutTemplates';
import { equipmentDatabase } from '../../data/equipment';
import SimpleLayoutEditor from './SimpleLayoutEditor';
import type { SimpleLayout } from '../../types';
import { buildPortEndpointId, buildPortNumberRemaps, endpointMatchesLayoutPort } from '../../utils/portNamingSchemes';
import { findLayoutPort, layoutPortKey } from '../../utils/portLayout';
import { endpointAtCableCapacity, maxCablesForPortKind, countCablesOnLayoutPort } from '../../utils/portConnectionLimits';
import { portKindDisplayName } from '../../utils/customPortTypes';
import { portHoverTooltip } from '../../utils/poeRj45Look';
import { resolveDeviceModel } from '../../engine/networkLogic';

// Face metadata for UI
const FACE_INFO: Record<EquipmentFace, { label: string; icon: string }> = {
  front: { label: 'Front', icon: '⬜' },
  back: { label: 'Back', icon: '⬛' },
  left: { label: 'Left', icon: '◀' },
  right: { label: 'Right', icon: '▶' },
  top: { label: 'Top', icon: '⬆' },
  bottom: { label: 'Bottom', icon: '⬇' },
};

// Function to get manufacturer badge color
const getManufacturerBadgeColor = (manufacturer: string, isDark: boolean) => {
  const normalized = manufacturer.toLowerCase();
  switch (normalized) {
    case 'ubiquiti':
    case 'ui':
      return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
    case 'cisco':
      return isDark ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-800';
    case 'netgear':
      return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    case 'tp-link':
    case 'tplink':
      return isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800';
    case 'd-link':
    case 'dlink':
      return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
    case 'linksys':
      return isDark ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
    case 'aruba':
      return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    case 'ruckus':
      return isDark ? 'bg-pink-900/40 text-pink-300' : 'bg-pink-100 text-pink-800';
    case 'meraki':
      return isDark ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-800';
    case 'fortinet':
      return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    default:
      return isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-800';
  }
};

interface PortsViewProps {
  equipment: PlacedEquipment;
  isOpen: boolean;
  onClose: () => void;
  onPortSelect: (portId: string) => void;
  embedded?: boolean;
  selectMode?: boolean;
  titleOverride?: string;
  allowedCableId?: string;
  onOpenEquipment?: (targetEquipment: PlacedEquipment) => void;
  highlightPort?: number | null;
  highlightKind?: string | null;
}

const PortsView: React.FC<PortsViewProps> = ({ equipment, isOpen, onClose, onPortSelect, embedded = false, selectMode = false, titleOverride, allowedCableId, onOpenEquipment, highlightPort, highlightKind }) => {
  const { createCableRun, isCableRunMode, selectedSourceEquipment, setSelectedSourceEquipment, updateEquipmentConfig, appTheme, remapPlacedEquipmentPortNumbers, colourizePorts, updateCableRun } = useProjectStore() as any;
  const isDark = appTheme === 'dark';
  const cardBg = isDark ? '#20242b' : '#ffffff';
  const stageBg = isDark ? '#171a1f' : '#f3f4f6';
  const border = isDark ? '#343a43' : '#e5e7eb';
  const text = isDark ? '#e5e7eb' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';
  const divider = isDark ? '#2d3340' : '#e5e7eb';
  const chipBg = isDark ? '#262b35' : '#f3f4f6';
  const liveEquipment = useProjectStore(state => {
    const design = state.getCurrentDesign();
    return design?.equipment.find(eq => eq.id === equipment.id);
  }) as PlacedEquipment | undefined;
  const cables = useProjectStore(state => state.getCurrentDesign()?.cables || []);
  const designEquipment = useProjectStore(state => state.getCurrentDesign()?.equipment || []);
  const { replaceEquipment } = useProjectStore() as any;
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [hoverPortId, setHoverPortId] = useState<string | null>(null);
  const [lastHoveredPortId, setLastHoveredPortId] = useState<string | null>(null);
  const [localConfig, setLocalConfig] = useState((liveEquipment?.config || equipment.config) || {});
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'equipment';
    id: string;
    label: string;
  } | null>(null);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);
  
  // Multi-face state
  const hasMultiFace = !!(localConfig?.layout?.multiFace || equipment?.config?.layout?.multiFace);
  const [activeFace, setActiveFace] = useState<EquipmentFace>('front');

  useEffect(() => { setLayoutVersion(v => v + 1); }, [(equipment as any)?.config?.layout?.simple]);
  
  // Sync local config with equipment config
  useEffect(() => {
    setLocalConfig((liveEquipment?.config || equipment.config) || {});
  }, [liveEquipment?.config, equipment.config]);

  // Force PortStage to refresh when layout changes locally
  useEffect(() => {
    setLayoutVersion(v => v + 1);
  }, [JSON.stringify(localConfig?.layout)]);

  // Calculate port count and layout BEFORE early return (hooks must be called unconditionally)
  const portCount = (liveEquipment?.equipment.specifications?.ports || equipment.equipment.specifications?.ports) || 8;
  
  // Get layout - prioritize localConfig for immediate updates, with layoutVersion forcing recalculation
  const simpleLayout = React.useMemo(() => {
    // Check for multi-face layout first
    if (hasMultiFace) {
      const multiFaceLayout = localConfig?.layout?.multiFace || liveEquipment?.config?.layout?.multiFace || equipment?.config?.layout?.multiFace;
      if (multiFaceLayout?.faces?.[activeFace]) {
        return multiFaceLayout.faces[activeFace] as SimpleLayout;
      }
      // If active face has no layout, try to find any face with layout
      const availableFaces = Object.keys(multiFaceLayout?.faces || {}) as EquipmentFace[];
      if (availableFaces.length > 0 && multiFaceLayout) {
        return multiFaceLayout.faces[availableFaces[0]] as SimpleLayout;
      }
    }
    
    // Fall back to simple layout
    return (localConfig?.layout?.simple || liveEquipment?.config?.layout?.simple || equipment?.config?.layout?.simple) as SimpleLayout | undefined;
  }, [localConfig?.layout, liveEquipment?.config?.layout, equipment?.config?.layout, layoutVersion, hasMultiFace, activeFace]);
  
  const layoutToRender: SimpleLayout = simpleLayout || makeTemplateForPorts(portCount);

  const highlightPortId = React.useMemo(() => {
    if (highlightPort == null) return null;
    const found = findLayoutPort(layoutToRender?.ports, highlightPort, highlightKind);
    return found ? layoutPortKey(found) : null;
  }, [highlightPort, highlightKind, layoutToRender]);
  
  // Get available faces for multi-face equipment
  const availableFaces = React.useMemo(() => {
    if (!hasMultiFace) return [];
    const multiFaceLayout = localConfig?.layout?.multiFace || liveEquipment?.config?.layout?.multiFace || equipment?.config?.layout?.multiFace;
    return Object.keys(multiFaceLayout?.faces || {}) as EquipmentFace[];
  }, [hasMultiFace, localConfig?.layout, equipment?.config?.layout]);

  // Determine active (used) ports by scanning cables - can be done after hooks
  const usedPortIds: string[] = [];
  const portConnections: Record<string, { cable: any; connectedEquipment: any }> = {};
  
  if (cables.length > 0) {
    for (const port of (layoutToRender?.ports || [])) {
      const portKey = layoutPortKey(port);
      const cable = cables.find((c: any) =>
        endpointMatchesLayoutPort(c.from, equipment.id, port, layoutToRender.ports) ||
        endpointMatchesLayoutPort(c.to, equipment.id, port, layoutToRender.ports)
      );
      if (cable) {
        usedPortIds.push(portKey);
        const otherEndId = endpointMatchesLayoutPort(cable.from, equipment.id, port, layoutToRender.ports) ? cable.to : cable.from;
        const otherEquipment = designEquipment.find((eq: any) => 
          otherEndId.startsWith(eq.id) || otherEndId.startsWith(eq.equipment.id)
        );
        if (otherEquipment) {
          portConnections[portKey] = { cable, connectedEquipment: otherEquipment };
        }
      }
    }
  }
  const detailsPortId = hoverPortId ?? lastHoveredPortId;
  const detailsPort = detailsPortId
    ? (layoutToRender?.ports || []).find((p) => layoutPortKey(p) === detailsPortId)
    : undefined;
  const detailsLink = detailsPortId ? portConnections[detailsPortId] : undefined;
  const detailsConnected = !!(detailsPortId && usedPortIds.includes(detailsPortId));

  if (!isOpen && !embedded) return null;

  const handlePortClick = (num: number, kind?: string) => {
    const portId = buildPortEndpointId(equipment.id, num, {
      kind,
      face: hasMultiFace ? activeFace : undefined,
    });
    
    // If we're in cable run mode and have a source equipment, create cable
    if (isCableRunMode && selectedSourceEquipment) {
      const sourcePortId = selectedSourceEquipment.id;
      
      // Check if ports are already in use
      const sourceOccupied = endpointAtCableCapacity(cables, sourcePortId);
      const destCount = countCablesOnLayoutPort(
        cables,
        equipment.id,
        { number: num, kind },
        layoutToRender?.ports || []
      );
      if (sourceOccupied || destCount >= maxCablesForPortKind(kind)) {
        alert('One or both ports are already at capacity. Please select different ports.');
        return;
      }
      
      console.log('Creating cable from port:', sourcePortId, 'to port:', portId);
      createCableRun(sourcePortId, portId, 'ethernet', `Port ${num}`, 'Cat6');
      setSelectedSourceEquipment(null);
      onClose();
    } else if (isCableRunMode) {
      // No source equipment, set this as the source
      const sourceEquipment = {
        ...equipment,
        id: portId
      };
      setSelectedSourceEquipment(sourceEquipment);
      onClose();
    } else {
      if (selectMode && allowedCableId) {
        const clickedPort = findLayoutPort(layoutToRender.ports, num, kind);
        const currentPort = highlightPort != null
          ? findLayoutPort(layoutToRender.ports, highlightPort, highlightKind)
          : undefined;
        const currentCable = cables.find((c: any) => c.id === allowedCableId);
        const occupiedBy = clickedPort
          ? cables.find((c: any) =>
              c.id !== allowedCableId && (
                endpointMatchesLayoutPort(c.from, equipment.id, clickedPort, layoutToRender.ports) ||
                endpointMatchesLayoutPort(c.to, equipment.id, clickedPort, layoutToRender.ports)
              )
            )
          : undefined;
        if (occupiedBy && currentCable && currentPort && clickedPort) {
          const occupiedSide: 'from' | 'to' = endpointMatchesLayoutPort(
            occupiedBy.from,
            equipment.id,
            clickedPort,
            layoutToRender.ports,
          ) ? 'from' : 'to';
          const currentSide: 'from' | 'to' = endpointMatchesLayoutPort(
            currentCable.from,
            equipment.id,
            currentPort,
            layoutToRender.ports,
          ) ? 'from' : 'to';
          updateCableRun(occupiedBy.id, { [occupiedSide]: currentCable[currentSide] });
          onPortSelect(occupiedBy[occupiedSide]);
          return;
        }
      }
      onPortSelect(portId);
    }
  };

  const Panel = (
    <div
      className={`rounded-lg flex flex-col overflow-hidden ${embedded ? 'w-full h-full' : 'w-[90vw] max-w-[1000px] h-[80vh]'}`}
      style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
    >
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: `1px solid ${border}` }}>
          <div className="flex items-start min-w-0">
            <CableIcon size={13} className="mr-1.5 mt-0.5 flex-shrink-0" style={{ color: muted }} />
            <div className="leading-tight min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold truncate" style={{ color: text }}>{titleOverride || (liveEquipment?.equipment.name || equipment.equipment.name)}</span>
                {highlightPort !== null && highlightPort !== undefined && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0"
                    style={{
                      backgroundColor: isDark ? 'rgba(234,88,12,0.25)' : '#ffedd5',
                      color: isDark ? '#fdba74' : '#c2410c',
                    }}
                  >
                    Port {highlightPort}
                  </span>
                )}
              </div>
              <div className="inline-block mt-1">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${getManufacturerBadgeColor((liveEquipment?.equipment.manufacturer || equipment.equipment.manufacturer || ''), isDark)}`}>
                  {(liveEquipment?.equipment.manufacturer || equipment.equipment.manufacturer || '').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        {!embedded && (
          <div className="flex items-center gap-1.5">
            {/* Existing toggle in header (live from store) */}
            {(() => {
              const live = (useProjectStore.getState() as any).getCurrentDesign()?.equipment?.find((eq: any) => eq.id === equipment.id);
              const isExisting = live ? !!live.isExisting : !!(equipment as any).isExisting;
              return (
                <button 
                  onClick={() => {
                    const { toggleEquipmentExistingStatus } = useProjectStore.getState() as any;
                    toggleEquipmentExistingStatus(equipment.id);
                  }}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors border ${
                    isExisting
                      ? (isDark ? 'bg-green-900/40 text-green-400 border-green-800' : 'bg-green-100 text-green-700 border-green-200')
                      : (isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-600 border-gray-200')
                  }`}
                  title={isExisting ? 'Mark as new infrastructure' : 'Mark as existing infrastructure'}
                >
                  {isExisting ? '🟢 Existing' : '⚪ New'}
                </button>
              );
            })()}
            {/* Edit Layout Button */}
            <button 
              onClick={() => setIsEditorOpen(true)}
              className="p-1.5 rounded transition-colors" 
              title="Edit Port Layout"
              style={{ color: muted }}
            >
              <Edit size={13} />
            </button>
            <button 
              onClick={() => {
                setConfirmDelete({ 
                  type: 'equipment', 
                  id: equipment.id, 
                  label: equipment.equipment.name 
                });
              }} 
              className="p-1.5 rounded transition-colors" 
              title="Delete equipment"
              style={{ color: isDark ? '#fca5a5' : '#dc2626' }}
            >
              <Trash2 size={13} />
            </button>
            <button 
              onClick={() => setIsReplaceOpen(true)} 
              className="p-1.5 rounded transition-colors" 
              title="Replace equipment"
              style={{ color: muted }}
            >
              <ArrowLeftRight size={13} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded transition-colors" title="Close" style={{ color: muted }}>
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0">
        <div className="p-2.5 overflow-hidden">
          {hasMultiFace && availableFaces.length > 0 && (
            <div className="mb-2 flex items-center gap-1.5 justify-center">
              {availableFaces.map((face) => {
                const faceInfo = FACE_INFO[face];
                const multiFaceLayout = localConfig?.layout?.multiFace || equipment?.config?.layout?.multiFace;
                const facePortCount = multiFaceLayout?.faces?.[face]?.ports?.length || 0;
                
                return (
                  <button
                    key={face}
                    onClick={() => setActiveFace(face)}
                    className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${
                      activeFace === face
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    title={`${faceInfo.label} - ${facePortCount} ports`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{faceInfo.label}</span>
                      {facePortCount > 0 && (
                        <span className={`text-[10px] px-1 py-0 rounded ${
                          activeFace === face ? 'bg-blue-500' : (isDark ? 'bg-gray-600' : 'bg-gray-200')
                        }`}>
                          {facePortCount}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          
          <div id="equipment-modal-stage" key={layoutVersion} className="relative flex justify-center rounded p-2" style={{ backgroundColor: stageBg }}>
            <PortStage 
              layout={layoutToRender}
              matchEditor
              colourizePorts={colourizePorts !== false}
              showTypeLabel={false}
              showTypeIcon={false}
              showConnectedRing={false}
              activePortIds={usedPortIds}
              onPortClick={handlePortClick}
              onPortHover={(_num, rect) => {
                const id = rect ? layoutPortKey(rect) : null;
                setHoverPortId(id);
                if (id) setLastHoveredPortId(id);
              }}
              hoveredPortId={hoverPortId}
              selectedPortId={highlightPortId}
              showMovePreview={selectMode}
              getPortTooltip={selectMode ? (port, connected) => {
                const toLabel = (port.label || String(port.number)).trim();
                if (highlightPortId && layoutPortKey(port) === highlightPortId) {
                  return { title: `Port ${toLabel}`, lines: ['Current cable port'] };
                }
                if (highlightPort == null) return portHoverTooltip(port, connected);
                const fromPort = findLayoutPort(layoutToRender.ports, highlightPort, highlightKind);
                const fromLabel = (fromPort?.label || String(highlightPort)).trim();
                if (connected) {
                  return {
                    title: 'Click to Swap Cables',
                    lines: [`${fromLabel} ↔ ${toLabel}`],
                  };
                }
                return {
                  title: 'Click to Move Cable here',
                  lines: [`${fromLabel} → ${toLabel}`],
                };
              } : undefined}
            />
          </div>


          {/* Port details — fixed height so hover does not resize the modal */}
          <div className="w-full mt-2 flex-shrink-0" style={{ borderTop: `1px solid ${divider}` }}>
            <div className="p-2.5 text-xs h-[148px]">
                <p className="text-[11px] uppercase tracking-wide font-medium mb-1.5" style={{ color: muted }}>
                  Port Details
                  <span className="normal-case tracking-normal font-normal ml-2" style={{ color: muted }}>
                    {portCount} ports · {usedPortIds.length} connected
                  </span>
                </p>
                <div className="space-y-1 text-xs" style={{ color: muted }}>
                    <div className="flex items-center gap-2 h-[22px]">
                      <span className="w-16 flex-shrink-0">Port:</span>
                      {detailsPort ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium truncate" style={{ backgroundColor: chipBg, color: text, border: `1px solid ${border}` }}>
                          {detailsPort.number}
                        </span>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 h-[22px]">
                      <span className="w-16 flex-shrink-0">Type:</span>
                      {detailsPort ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium truncate" style={{ backgroundColor: chipBg, color: text, border: `1px solid ${border}` }}>
                          {portKindDisplayName(detailsPort.kind)}
                        </span>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 h-[22px]">
                      <span className="w-16 flex-shrink-0">Status:</span>
                      {detailsPort ? (
                        detailsConnected ? (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-800'}`}>
                            Connected
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                            Open
                          </span>
                        )
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 h-[22px] min-w-0">
                      <span className="w-16 flex-shrink-0">Cable:</span>
                      {detailsConnected && detailsLink ? (
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium cursor-pointer truncate max-w-full ${isDark ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                          title={`Go To: ${detailsLink.cable.label || 'Unnamed Cable'}`}
                          onClick={() => {
                            const { setSelectedCable, setCablePortEditorOpen } = useProjectStore.getState();
                            onClose();
                            setSelectedCable(detailsLink.cable);
                            setCablePortEditorOpen(true);
                          }}
                        >
                          {detailsLink.cable.label || 'Unnamed Cable'}
                        </span>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 h-[22px] min-w-0">
                      <span className="w-16 flex-shrink-0">Equipment:</span>
                      {detailsConnected && detailsLink ? (
                        <span
                          className="font-medium cursor-pointer hover:text-blue-400 transition-colors truncate"
                          title={`Go To: ${detailsLink.connectedEquipment.config?.label || detailsLink.connectedEquipment.equipment.name}`}
                          onClick={() => {
                            onClose();
                            setTimeout(() => {
                              const { setSelectedEquipmentForPortView, setPortVisualizationOpen } = useProjectStore.getState() as any;
                              setSelectedEquipmentForPortView(detailsLink.connectedEquipment);
                              setPortVisualizationOpen(true);
                            }, 100);
                          }}
                        >
                          {detailsLink.connectedEquipment.config?.label
                            ? `${detailsLink.connectedEquipment.config.label} • ${detailsLink.connectedEquipment.equipment.name}`
                            : detailsLink.connectedEquipment.equipment.name}
                        </span>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (embedded) return <div className="w-full h-full">{Panel}</div>;
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      {Panel}

      {/* Replace Equipment Modal */}
      {isReplaceOpen && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center">
          <div className="rounded-lg shadow-2xl w-[520px] max-w-[92vw]" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
            <div className="px-4 py-2.5 text-sm font-semibold flex items-center justify-between" style={{ borderBottom: `1px solid ${border}`, color: text }}>
              <span>Replace Equipment</span>
              <button className="p-1 rounded" style={{ color: muted }} onClick={() => setIsReplaceOpen(false)}>✕</button>
            </div>
            <div className="p-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 gap-1.5">
                {equipmentDatabase.map((eq) => (
                  <button
                    key={eq.id}
                    onClick={() => {
                      (useProjectStore.getState() as any).replaceEquipment(equipment.id, eq);
                      setIsReplaceOpen(false);
                    }}
                    className="text-left px-2.5 py-2 rounded border transition-colors"
                    style={{ borderColor: border, color: text }}
                  >
                    <div className="font-medium text-xs">{eq.name}</div>
                    <div className="text-[11px]" style={{ color: muted }}>{eq.manufacturer} • {eq.type}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-4 py-2.5 flex justify-end gap-2" style={{ borderTop: `1px solid ${border}` }}>
              <button className="px-3 py-1.5 text-xs rounded border" style={{ borderColor: border, color: muted }} onClick={() => setIsReplaceOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Simple Layout Editor */}
      {isEditorOpen && (
        <SimpleLayoutEditor
          onSave={(layout, equipmentData, saveTarget) => {
            const prevLayout = localConfig?.layout || equipment.config?.layout;
            const remaps = buildPortNumberRemaps(prevLayout, layout);
            const deviceModel = equipmentData?.deviceModel;
            const patch = { layout, ...(deviceModel ? { deviceModel } : {}) };
            setLocalConfig(prev => ({ ...prev, ...patch }));
            setLayoutVersion(v => v + 1);
            if (saveTarget === 'library' && equipment.equipment.id) {
              const { saveEquipmentConfig, getEquipmentConfig } = useProjectStore.getState() as any;
              const existingConfig = getEquipmentConfig(equipment.equipment.id);
              const mergedConfig = existingConfig
                ? { ...existingConfig, ...patch }
                : { ...patch };
              saveEquipmentConfig(equipment.equipment.id, mergedConfig, { updateExistingInstances: true });
            } else {
              const { remapPlacedEquipmentPortNumbers } = useProjectStore.getState() as any;
              remapPlacedEquipmentPortNumbers(equipment.id, remaps, {
                nextLayout: layout,
                detachOrphans: true,
              });
              updateEquipmentConfig(equipment.id, patch, { persistToLibrary: false });
            }
            useProjectStore.getState().saveProject();
            setIsEditorOpen(false);
          }}
          onClose={() => setIsEditorOpen(false)}
          equipmentName={equipment.equipment.name}
          equipmentManufacturer={liveEquipment?.equipment.manufacturer || equipment.equipment.manufacturer}
          equipmentLabel={liveEquipment?.config?.label || equipment.config?.label}
          initialLayout={
            localConfig?.layout ||
            equipment.config?.layout ||
            resolveDeviceModel(
              equipment.equipment.id,
              equipment.equipment.type,
              localConfig || equipment.config
            ).model.layout
          }
          portCount={portCount}
          showDualSave
          enableElectrical
          initialDeviceModel={resolveDeviceModel(
            equipment.equipment.id,
            equipment.equipment.type,
            localConfig || equipment.config
          ).model}
          equipmentType={equipment.equipment.type}
          equipmentId={equipment.equipment.id}
        />
      )}

      {/* Modern Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center">
          <div className="rounded-lg shadow-2xl w-[360px] max-w-[92vw]" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
            <div className="px-4 py-2.5 text-sm font-semibold" style={{ borderBottom: `1px solid ${border}`, color: text }}>
              Delete equipment?
            </div>
            <div className="p-4 text-xs" style={{ color: muted }}>
              Are you sure you want to delete <span className="font-medium" style={{ color: text }}>{confirmDelete.label}</span> and its connected cables? This action cannot be undone.
            </div>
            <div className="px-4 py-2.5 flex justify-end gap-2" style={{ borderTop: `1px solid ${border}` }}>
              <button 
                className="px-3 py-1.5 text-xs rounded border" 
                style={{ borderColor: border, color: muted }}
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={() => {
                  (useProjectStore.getState() as any).removeEquipment(confirmDelete.id);
                  setConfirmDelete(null);
                  onClose();
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortsView;
